// Real-app entry — the one impure mount for `index.html`. Hands the pure
// `AppShellHost` the concrete `httpPorts` (fetch against the running server)
// through the `AppPorts` seam. (The former backendless `main.tsx`/`app.html`
// preview on `mockPorts` was retired — the desktop shell dogfoods this same
// path against an in-process server.)

// First: must run before any module builds a schema (see the file).
import "./zodJitless.js";
import { exhaustSwitchCase } from "@bilda/utils";
import { I18nProvider } from "@lingui/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { activateLocale, detectLocale, i18n } from "../i18n/setup.js";
import { AppShellHost } from "./AppShellHost.js";
import { AiUsageHost } from "./aiUsage/AiUsageHost.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { FeedbackAdminHost } from "./feedbackAdmin/FeedbackAdminHost.js";
import { httpPorts } from "./httpPorts.js";
import { OpsAccountsHost } from "./ops/OpsAccountsHost.js";
import { OpsOverviewHost } from "./ops/OpsOverviewHost.js";
import { type OpsPage, opsPageOf, opsPathOfLegacy } from "./ops/route.js";
import { AppPortsProvider } from "./ports.js";
import { ThemeProvider } from "./theme.js";

// The dev-only event-log fixture switcher (DevLogPicker) is no longer mounted
// here: it now lives INSIDE Settings → Advanced (see AppShellHost), reachable
// but never floating on the main canvas.

const container = document.getElementById("root");
if (container === null) {
    throw new Error("app root element missing");
}

// Impure: pick and activate the locale before the tree renders.
activateLocale(detectLocale());

// Impure: an old /admin/… link moves to its /ops/… address first.
const moved = opsPathOfLegacy(window.location.pathname, window.location.search);
if (moved !== undefined) window.history.replaceState(null, "", moved);

// The operator's Ops pages are their own small tree (the Ops menu, no app
// shell); every other path is the app.
const opsHost = (p: OpsPage) => {
    switch (p) {
        case "overview":
            return <OpsOverviewHost />;
        case "feedback":
            return <FeedbackAdminHost />;
        case "ai-usage":
            return <AiUsageHost />;
        case "accounts":
            return <OpsAccountsHost />;
        default:
            return exhaustSwitchCase(p);
    }
};
const ops = opsPageOf(window.location.pathname);
const page =
    ops !== undefined ? (
        opsHost(ops)
    ) : (
        <AppPortsProvider value={httpPorts}>
            <AppShellHost />
        </AppPortsProvider>
    );

createRoot(container).render(
    <StrictMode>
        <I18nProvider i18n={i18n}>
            <ErrorBoundary>
                <ThemeProvider>{page}</ThemeProvider>
            </ErrorBoundary>
        </I18nProvider>
    </StrictMode>,
);
